import {
  PagedList,
  Query,
  ZenqlQuery as ZenqlQueryType,
} from 'contensis-core-api';
import { Entry, ZenqlQuery } from 'contensis-management-api';
import pl from 'pluralize';

import EntriesDependencyService, {
  EntryDependencyCheck,
} from './EntriesDependencyService';
import RepositoryBaseService from './RepositoryBaseService';
import { searchQuery } from '~/providers/ContensisManagementApi';
import ContensisRepository from '~/repositories/ContensisRepository';
import { AssetEntryBuilder } from '~/transformations/AssetEntryBuilder';
import { EntryBuilder } from '~/transformations/EntryBuilder';
import { PartialEntryBuilder } from '~/transformations/PartialEntryBuilder';
import { loadEntryIds, mergeEntryIds } from '~/util/EntryHelper';
import {
  Logger,
  completeProgressBar,
  newProgressBar,
  progress,
} from '~/util/logger';
import { isArray, isNumber } from '~/util/typeguards';

import { DataFormats } from '~/models/Enums';
import { BuildEntryOptions } from '~/models';

type SearchEntries = (
  query: Query | ZenqlQueryType,
  linkDepth?: number
) => Promise<PagedList<Entry>>;

class EntriesService extends RepositoryBaseService {
  private searchEntries: SearchEntries;
  private useDelivery: boolean;

  dependencies: EntriesDependencyService;
  /**
   * Keep a record of built entries to migrate
   */
  migrate: (EntryBuilder | AssetEntryBuilder)[] = [];
  raw: Entry[] = [];
  stopAtLevel?: number | undefined;

  /** List of entryIds to resolve in a target repo for an entries migration */
  get entryIds() {
    const theseIds = new Set(this.raw.map((e) => e.sys.id || ''));
    const stopLevel = this.contensis.context.payload.stopLevel;
    // if there is a stop level set and we are in a source repo,
    // add the dependent entry id links from the final level we fetched
    // so we can try to resolve them in the target repo and avoid broken links
    // if dependencies from the final level of built dependent entries
    // already exist in the target repo
    if (isNumber(stopLevel) && this.isSourceRepo) {
      const finalLevel = this.dependencies.dependencyLevels[stopLevel];

      for (const { entryIds } of Object.values(finalLevel || {}).filter(
        (v) => !isNumber(v)
      ) as EntryDependencyCheck[]) {
        for (const entryId of entryIds.map(({ entryId }) => entryId)) {
          theseIds.add(entryId);
        }
      }
    }
    return [...theseIds];
  }

  constructor(contensis: ContensisRepository, source: ContensisRepository) {
    super(contensis, source);
    this.outputProgress = source.context.payload.outputProgress || false;

    // use the delivery api only on source repositories
    this.useDelivery = !!(
      contensis === source && source.context.payload.query?.useDelivery
    );
    this.searchEntries = (
      this.useDelivery
        ? contensis.repo?.DeliverySearchEntries
        : contensis.repo?.SearchEntries
    ) as SearchEntries;

    // create new instance of EntriesDependencyService so
    // we can call the dependency analysis methods when building
    // up lists of entries to query for migration
    this.dependencies = new EntriesDependencyService(contensis);
    this.stopAtLevel = source.context.payload.stopLevel;
  }
  /**
   * Find a dependent entry from the modeling context or resolve from the fetched entries
   * @param value the source entry to find
   * @returns the found entry or undefined
   */
  findDependent = (value: Entry) => {
    const findId = this.contensis.transformGuid(value?.sys?.id || '');
    if (this.contensis.context.entries.length) {
      const entryInContext = this.contensis.context.entries.find(
        (e) => e.sys.id === findId
      );
      if (entryInContext) return entryInContext;
    }
    return this.resolve(findId);
  };

  /**
   * Resolves an entry we have fetched
   * @param entryId the id of the entry to resolve
   * @returns the resolved entry or undefined if not found
   */
  resolve = (entryId: string) => this.raw.find((e) => e.sys.id === entryId);

  /**
   * Resolves an entry we have fetched in the source repository
   * to cover cases where we have stopped fetching dependencies at a certain level
   * so we can infer the entry will be migrated from the source repository
   * rather than omitting a link when that entry is actually built and migrated
   * at the same time as this entry
   * @param originalId the id of the entry to resolve from the source repository
   * @returns the resolved entry or undefined if not found
   */
  resolveInSource = (originalId: string) =>
    this.source.entries.raw.find((e) => e.sys.id === originalId);

  GetEntries = async (entryIds: string[] = []) => {
    return this.GetEntriesIncludingDependents({ entryIds }, false);
  };

  GetEntriesIncludingDependents = async (
    getBy: {
      assetUris?: string[];
      entryIds?: string[];
    } = {},
    withDependents = true
  ) => {
    const { assetUris = [], entryIds = [] } = getBy;

    Logger.info(
      `${
        entryIds?.length
          ? `Finding ${entryIds.length}`
          : 'Searching for initial'
      } ${pl('entry', entryIds?.length || 0)}${this.useDelivery ? ' with Delivery API' : ''} in ${
        this.isSourceRepo && !entryIds?.length ? '' : 'target environment '
      }${Logger.highlightText(this.contensis.alias)} ${Logger.standardText(
        this.contensis.projectId
      )}`,
      true
    );

    const loadedEntryIds: ContensisRepository['entryIds'] = [];
    const batchSize = 100;

    const outputProgress =
      this.outputProgress && (!entryIds?.length || entryIds.length > batchSize);

    if (assetUris.length > 0) {
      // Load the found assets by uri

      if (outputProgress)
        newProgressBar(
          ` ${this.useDelivery ? 'Delivery' : 'Fetch'} [${this.contensis.project.id}] :bar :percent ${Logger.infoText(
            ':total :elapseds/:etas :ratep/s'
          )}`,
          {
            output:
              this.outputProgress &&
              (!assetUris.length || assetUris.length > batchSize),
            total: assetUris.length || 0,
          }
        );
      Logger.info(
        `Fetching dependent assets by ${pl('path', assetUris.length, true)} in project '${this.contensis.projectId}'`,
        true
      );

      const assetEntries = await this.GetEntriesAllPages(
        50,
        {
          // const assetEntries = await this.GetEntriesAllPages(100, {
          assetUris: assetUris.map((uri) => `${uri}.`),
        },
        outputProgress
      );

      loadEntryIds(loadedEntryIds, assetEntries);

      // perf: to do the job of merge and dedupeInner and maintain
      // the entryIds index in one loop
      mergeEntryIds(
        this.contensis.entryIds,
        loadedEntryIds,
        this.raw,
        assetEntries
      );
    }

    if (outputProgress)
      newProgressBar(
        ` ${this.useDelivery ? 'Delivery' : 'Fetch'} [${this.contensis.project.id}] :bar :percent ${Logger.infoText(
          ':total :elapseds/:etas :ratep/s'
        )}`,
        {
          output:
            this.outputProgress &&
            (!entryIds?.length || entryIds.length > batchSize),
          total: entryIds.length || 0,
        }
      );
    const entries = await this.GetEntriesAllPages(
      batchSize,
      { entryIds },
      outputProgress
    );

    loadEntryIds(loadedEntryIds, entries);

    // perf: to do the job of merge and dedupeInner and maintain
    // the entryIds index in one loop
    mergeEntryIds(this.contensis.entryIds, loadedEntryIds, this.raw, entries);

    // Check each entry for dependent entries
    // only if we are limiting by ContentType or entryIds
    if (
      withDependents &&
      (this.contensis.context.getContentTypeIds().length ||
        this.contensis.query.includeIds?.length)
    )
      await this.CheckDependentEntriesAndNodes(entries, {
        level: 1,
        getEntries: withDependents,
      });
    return this.raw;
  };

  GetEntriesAllPages = async (
    pageSize = 200,
    getBy?: { assetUris?: string[]; entryIds?: string[] },
    outputProgress = false,
    versionStatus = !this.isSourceRepo ? 'latest' : this.contensis.versionStatus
  ) => {
    const { assetUris = [], entryIds = [] } = getBy || {};

    let pageIndex = 0;
    let fetchMore = true;
    const items: Entry[] = [];

    // The while loop is supposed to fetch all entries until
    // we have retrieved the totalCount
    while (fetchMore) {
      // These indices allow us to slice a supplied list of ids
      // and page through the id list to give us the found results
      const startIndex = pageIndex * pageSize;
      const endIndex = Math.min(
        entryIds.length || assetUris.length,
        (pageIndex + 1) * pageSize
      );

      const { items: entries, totalCount } = await this.GetEntriesPage(
        pageSize,
        entryIds.length > 0 ? 0 : pageIndex,
        {
          assetUris: assetUris.slice(startIndex, endIndex),
          entryIds: entryIds?.slice(startIndex, endIndex),
        },
        versionStatus
      );
      if (outputProgress) {
        if (!endIndex) {
          progress.current.total = totalCount;
        } else if (pageIndex === 0 && endIndex > pageSize) {
          progress.current.total = entryIds.length + assetUris.length;
        }
        //if (pageIndex !== 0 || (pageIndex === 0 && endIndex >= pageSize))
        progress.current.tick(pageSize);
      }

      items.push(...entries);

      if (
        (endIndex && endIndex >= (entryIds.length || assetUris.length)) ||
        (!endIndex && items.length >= totalCount)
      )
        fetchMore = false;
      else pageIndex++;
    }
    completeProgressBar();

    // If fetching a list of entryIds (i.e. dependents of an entry returned in the initial query)
    // Check how many are not returned and repeat the query looking for "latest"
    // content versionStatus to prevent error `The entry link does not exist` during import
    if (this.isSourceRepo && entryIds.length && versionStatus !== 'latest') {
      const notFoundIds: string[] = [];
      for (const id of entryIds) {
        if (!items.find((e) => e.sys.id === id)) notFoundIds.push(id);
      }
      if (notFoundIds.length) {
        Logger.info(
          `Refetch ${notFoundIds.length} unresolved dependent ${pl('entry', notFoundIds.length)} with 'versionStatus: latest' in ${
            this.isSourceRepo ? '' : 'target environment '
          }${Logger.highlightText(this.contensis.alias)} ${Logger.standardText(
            this.contensis.projectId
          )}`,
          true
        );
        newProgressBar(
          ` ${this.useDelivery ? 'Delivery' : 'Fetch'} [${
            this.contensis.project.id
          }] :bar :percent ${Logger.infoText(
            ':total :elapseds/:etas :ratep/s'
          )}`,
          {
            output: this.outputProgress && notFoundIds.length > pageSize,
            total: notFoundIds.length || 0,
          }
        );

        const unpublishedEntries: Entry[] = await this.GetEntriesAllPages(
          pageSize,
          { entryIds: notFoundIds },
          this.outputProgress,
          'latest'
        );
        completeProgressBar();

        // Log any entry ids that are still unresolved in source repos only
        // as in target repos they may not exist yet
        if (unpublishedEntries.length < notFoundIds.length) {
          const stillNotFoundIds: string[] = [];
          for (const id of notFoundIds) {
            if (!unpublishedEntries.find((e) => e.sys.id === id))
              stillNotFoundIds.push(id);
          }
          Logger.warning(
            `Unresolved dependent entries: ${stillNotFoundIds.join(', ')}`,
            true
          );
        }
        if (unpublishedEntries.length) return [...unpublishedEntries, ...items];
      }
    }
    return items;
  };

  GetEntriesPage = async (
    pageSize = 200,
    pageIndex = 0,
    getBy?: { assetUris?: string[]; entryIds?: string[] },
    versionStatus = this.contensis.versionStatus
  ) => {
    let query: Query | ZenqlQueryType;
    if (getBy?.assetUris?.length) {
      query = searchQuery({
        includePaths: getBy.assetUris,
        pageIndex,
        pageSize,
        versionStatus,
      });
    } else if (getBy?.entryIds?.length) {
      query = searchQuery({
        includeIds: getBy.entryIds,
        languages: this.contensis.query.languages,
        pageIndex,
        pageSize,
        versionStatus,
      });
    } else if (this.contensis.zenQL) {
      query = this.useDelivery
        ? new ZenqlQueryType(this.contensis.zenQL)
        : new ZenqlQuery(this.contensis.zenQL);
      query.pageIndex = pageIndex;
      query.pageSize = pageSize;
    } else {
      query = searchQuery({
        ...this.contensis.query,
        contentTypeIds: this.contensis.query?.contentTypeIds?.length
          ? this.contensis.query.contentTypeIds
          : this.contensis.context.getContentTypeIds() || [],
        dataFormats: this.contensis.query.dataFormats || [DataFormats.entry],
        pageIndex,
        pageSize,
      });
    }

    const pagedResult = await this.searchEntries(query);
    return pagedResult;
  };

  CheckDependentEntriesAndNodes = async (
    entries: Entry[],
    {
      level = 1,
      stopAtLevel = this.stopAtLevel,
      getEntries = true,
      getNodes = true,
    }
  ) => {
    // Record that we stopped at this level so we can check when building entries
    this.dependencies.stopLevel = stopAtLevel;

    // Find dependent ids (and their content type)
    const getDependentIds = new Map<string, string>();
    const getDependentAssets = new Map<string, string[]>();
    for (const entry of entries) {
      const dependentIds = this.dependencies.FindEntryDependencies(
        entry,
        level
      );
      if (getEntries) {
        // Check have we already resolved the found dependent entries
        for (const { contentTypeId, entryId: dependentId } of dependentIds) {
          // it is quicker to search this index of entryIds
          // than it is to search the array of raw entries
          if (
            this.contensis.entryIds.findIndex(
              ([, eId]) => dependentId === eId
            ) === -1
          ) {
            getDependentIds.set(dependentId, contentTypeId);
            // Also keep a record of any dependent assets found
            const existing = getDependentAssets.get(contentTypeId);
            if (existing) {
              existing.push(dependentId);
            } else {
              getDependentAssets.set(contentTypeId, [dependentId]);
            }
          }
        }
      }
    }

    const entryIds = Array.from(getDependentIds.keys()).filter((id) => id);

    if (entryIds.length > 0) {
      // Stop fetching dependencies at a certain level
      if (isNumber(stopAtLevel) && level > stopAtLevel) {
        const entriesAtLevel = this.dependencies.levels[level - 1];
        const dependenciesAtLevel =
          this.dependencies.dependencyLevels[level - 1];
        const requiredAtLevel = this.dependencies.requiredLevels[level - 1];
        // No entries at this level or
        // All entries at this level have no linked content
        // This may never be hit?
        if (
          entriesAtLevel.length === 0 ||
          (entriesAtLevel.length > 0 && entriesAtLevel.total === 0)
        ) {
          Logger.info(
            `Hit stop level ${level}, ${pl('entry', entriesAtLevel.length, true)} with ${pl('reference', entriesAtLevel.total, true)} to linked content.`
          );
          return;
        }
        // We have entries at this level with references to linked content
        if (entriesAtLevel.length > 0 && entriesAtLevel.total > 0) {
          const dependencyFieldIds = [
            ...(
              Object.values(entriesAtLevel).filter(
                (v) => !isNumber(v)
              ) as EntryDependencyCheck[]
            ).reduce((ids, r) => {
              for (const { contentTypeId, fieldIdPath } of r.entryIds)
                if (contentTypeId !== 'image')
                  ids.add(`${contentTypeId}.${fieldIdPath}`);
              return ids;
            }, new Set<string>()),
          ].sort();
          // none of these references are in required fields
          Logger.warning(
            `Hit stop level ${level}, ${dependenciesAtLevel.length}/${pl('entry', entriesAtLevel.length, true)} with ${pl('reference', entriesAtLevel.total, true)} to linked content in ${pl('field', dependencyFieldIds.length, true)} at this level. You may get incomplete content in the target environment for these fields ${Logger.infoText(`- ${[...dependencyFieldIds].join(', ')}`)}`
          );
          // these references include some that are in required fields
          if (requiredAtLevel.length > 0) {
            const requiredFieldIds = [
              ...(
                Object.values(requiredAtLevel).filter(
                  (v) => !isNumber(v)
                ) as EntryDependencyCheck[]
              ).reduce((ids, r) => {
                for (const f of Object.keys(r.requiredFields))
                  ids.add(`${r.entry?.sys.contentTypeId}.${f}`);
                return ids;
              }, new Set<string>()),
            ].sort();
            Logger.error(
              `${pl('entry', requiredAtLevel.length, true)} with ${pl('content link', requiredAtLevel.required, true)} in ${pl('required field', requiredFieldIds.length, true)} at this level - ${[...requiredFieldIds].join(', ')}`,
              true
            );
          }
        }
        // Images are counted as dependent entries so we should extract any
        // image entry ids and resolve them so they are included in the migration

        const imageEntryIds = getDependentAssets.get('image');
        if (imageEntryIds)
          await this.GetDependentEntries(
            imageEntryIds,
            entries.length,
            'entries',
            level
          );
      } else {
        // Load the found entries by id
        await this.GetDependentEntries(
          entryIds,
          entries.length,
          'entries',
          level
        );
      }
    } else if (getNodes) {
      // Handle found dependent node ids
      const newNodeIds = new Map<string, string>();

      // Only add nodes we have not yet resolved
      // for each nodeId in the entry
      for (const [, nodesMeta] of this.dependencies.nodeIds) // for each entryId
        for (const [nodeId, dependencyLevel, nodePath] of nodesMeta) {
          if (
            !isNumber(stopAtLevel) ||
            (isNumber(stopAtLevel) && dependencyLevel < stopAtLevel)
          ) {
            if (
              this.contensis.nodes.raw.findIndex((n) => n.id === nodeId) === -1
            )
              newNodeIds.set(nodeId, nodePath || '');
          }
        }

      if (newNodeIds.size > 0) {
        await this.GetDependentNodes(newNodeIds);

        const nodeEntryIds = new Set<string>();
        for (const node of this.contensis.nodes.raw) {
          const entryId = node.entry?.sys.id || (node as any).entryId;
          // Pull out any entries from found nodes (that we don't already have)
          if (entryId && this.raw.findIndex((e) => e.sys.id === entryId) === -1)
            nodeEntryIds.add(entryId);
          // Update dependencies with extra data
          for (const [, nodeDep] of this.dependencies.nodeIds) {
            for (const dep of nodeDep) {
              if (dep[0] === node.id) {
                dep[2] = node.path;
                dep[3] = entryId;
              }
            }
          }
        }

        // Find any additional entries linked to nodes
        if (getEntries && nodeEntryIds.size > 0)
          await this.GetDependentEntries(
            Array.from(nodeEntryIds),
            this.contensis.nodes.raw.length,
            'nodes',
            level
          );
      }
    }
  };

  GetDependentEntries = async (
    entryIds: string[],
    count: number,
    what: 'entries' | 'nodes' | 'content types',
    level = 1,
    stopAtLevel = this.stopAtLevel
  ) => {
    // Get any found dependent entries
    if (entryIds.length > 0) {
      // Load the found entries by id
      Logger.info(
        `${level <= 1 ? `Fetching` : `Found a further`} ${pl(
          'dependent entry',
          entryIds.length,
          true
        )} from ${pl(what, count, true)} ${
          level <= 1
            ? `in ${
                this.isSourceRepo ? 'source' : 'target'
              } project ${Logger.standardText(this.contensis.projectId)}`
            : `at level ${level}`
        }`,
        true
      );
      newProgressBar(
        ` ${this.useDelivery ? 'Delivery' : 'Fetch'} [${this.contensis.project.id}] :bar :percent ${Logger.infoText(
          ':total :elapseds/:etas :ratep/s'
        )}`,
        { output: this.outputProgress, total: entryIds.length || 0 }
      );
      const dependentEntries = await this.GetEntriesAllPages(
        100,
        { entryIds },
        this.outputProgress
      );

      mergeEntryIds(
        this.contensis.entryIds,
        loadEntryIds([], dependentEntries),
        this.raw,
        dependentEntries
      );

      // Check the returned dependent entries for further dependent entries
      await this.CheckDependentEntriesAndNodes(dependentEntries, {
        level: level + 1,
        stopAtLevel,
      });
    }
  };

  GetDependentNodes = async (
    nodeIds: Map<string, string>,
    depth = 0,
    dependentOf = 'entries'
  ) => {
    const { nodes, projectId } = this.contensis;
    // Get any found dependent nodes

    const findNodes = new Map<string, string>();
    for (const [id, path] of nodeIds) {
      // Have we already resolved this node?
      const resolved = nodes.raw.find((n) => n.id === id || n.path === path);
      // Have we already resolved this node as an ancestor of another node?
      const resolvedAncestor = nodes.ancestors.find(
        (n) => n.id === id || n.path === path
      );

      if (resolved) {
        // Node is already resolved, do nothing
      } else {
        // Add a node resolved from ancestors to the nodes array
        if (resolvedAncestor) nodes.raw.push(resolvedAncestor);
        else findNodes.set(id, path);
      }
    }

    if (findNodes.size > 0) {
      // Load the found nodes by id
      Logger.info(
        `Fetching ${pl(
          'dependent node',
          findNodes.size,
          true
        )} from ${dependentOf} in ${
          this.isSourceRepo ? 'source' : 'target'
        } project ${Logger.standardText(projectId)}`,
        true
      );

      const pr = newProgressBar(
        ` Fetch [${projectId}] :bar :percent ${Logger.infoText(
          ':total :elapseds/:etas :ratep/s'
        )}`,
        {
          output: this.outputProgress && nodeIds.size > 2,
          total: nodeIds.size || 0,
        }
      );

      // Look them up (one by one)]
      // TODO: add pqueue concurrency
      if (this.source.repo) {
        for (const [id, path] of findNodes) {
          const node = await nodes.fetchTargetNodes(path, id, depth);
          // if (node && nodes.raw.findIndex((n) => node.parentId === n.id) === -1)
          //   await nodes.AppendNodeAncestors(node.id);
          pr?.tick();
        }
        await nodes.EnsureRootAncestor();
      }
      completeProgressBar();
    }
  };

  BuildMigrateEntries = async ({
    onError,
    outputProgress,
  }: BuildEntryOptions) => {
    const { contensis, source } = this;

    const entries = source.entries.raw.filter(
      (e) => e.sys.dataFormat !== 'asset'
    );
    if (entries.length)
      Logger.info(
        `Building ${pl('content entry', entries.length, true)}`,
        true
      );

    const pr = newProgressBar(
      ` Building entries [${
        this.contensis.project.id
      }] :bar :percent ${Logger.infoText(':total :elapseds/:etas :ratep/s')}`,
      {
        output: outputProgress && !!(entries.length >= 200),
        total: entries.length || 0,
      }
    );

    const Builder = contensis.context.payload?.partialEntries
      ? PartialEntryBuilder
      : EntryBuilder;

    for (const entry of entries) {
      // Build migrate entry
      const entryBuilder = new Builder(contensis, source, entry);
      entryBuilder.build();
      this.migrate.push(entryBuilder);
      if (entryBuilder.status === 'error') onError(entryBuilder);
      pr?.tick();
    }

    completeProgressBar();

    this.PruneMigrateEntries();

    // Log any warnings for entries we are migrating
    const warnings = this.migrate
      .filter((e) => e.warnings.length)
      .map((e) => [e.sourceEntry, e.warnings]) as [Entry, string[]][];

    if (warnings.length) {
      Logger.warning(
        `${pl('entry', warnings.length, true)} built with warnings:\n` +
          warnings
            .map(
              ([entry, ws]) =>
                `${this.contensis.logGuid(entry)} ${Logger.highlightText(entry.sys.contentTypeId)} ${entry.entryTitle}:${ws.map((w) => `\n  - ${w}`).join('')}`
            )
            .join('\n')
      );
    }
  };

  /** After building entries to migrate, prune any that are not needed
   * For example, if an entry is a dependency of another entry
   * that is not being migrated (status ignore or error)
   * and the dependency is not required by any other entry
   * we will ignore this entry too
   */
  PruneMigrateEntries = () => {
    const before = this.migrate.filter((e) => e.status !== 'ignore').length;
    // Filter all migrate entries for any with dirty dependencies
    const dirtyDependencies = this.migrate
      .filter((e) => e.dependencies.find((d) => d.isDirty))
      // Get a list of all dirty dependencies with a reference to their parent entry
      .map((e) =>
        e.dependencies
          .filter((d) => d.isDirty)
          .map((d) => ({ ...d, parentId: e.originalId }))
      )
      .flat();

    // Produce a list of potential entries to ignore based on dirty dependencies
    const entriesToIgnore = this.migrate.filter((e) =>
      dirtyDependencies.find((d) => d.entryId === e.originalId)
    );

    for (const entry of entriesToIgnore) {
      const dirtyDependency = dirtyDependencies.find(
        (d) => d.entryId === entry.originalId
      );
      if (!dirtyDependency) continue;

      // Check the migrate entry to see if is a dependency of any other entry
      const otherDependents = entry.dependencyOf.filter(
        (d) =>
          // does not share the same parent as the dirty dependency
          d.entryId !== dirtyDependency.parentId ||
          // is a dependency on another field in the same parent entry
          (d.entryId === dirtyDependency.parentId &&
            d.fieldIdPath !== dirtyDependency.fieldIdPath)
      );

      if (!otherDependents.length) {
        entry.status = 'ignore';
        // override any previous warnings with just the ignore reason
        if (Logger.shouldLog('info'))
          entry.warnings = [
            `Excluded ${Logger.infoText(`[dependency of entry ${this.contensis.logGuid({ sys: { id: dirtyDependency.parentId } })} was not mapped into field ${dirtyDependency.fieldIdPath}]`)}`,
          ];

        this.PruneIgnoredEntryDependencies(entry);
      }
    }

    // Filter all migrate entries for any with error status and check to see can we ignore their dependencies
    const errorEntries = this.migrate.filter((e) => e.status === 'error');
    for (const entry of errorEntries) {
      this.PruneIgnoredEntryDependencies(entry);
    }

    const after = this.migrate.filter((e) => e.status !== 'ignore').length;
    if (before !== after)
      Logger.info(
        `Excluded ${pl('dependency entry', before - after, true)} due to ${pl('entry', dirtyDependencies.length + errorEntries.length, true)} not being migrated`,
        true
      );
  };

  /**
   * Recursive check an excluded entry for any dependencies and if they are not
   * required anywhere else mark those as ignored too
   * @param entry an entry that has been marked as ignore
   */
  PruneIgnoredEntryDependencies = (entry: EntryBuilder) => {
    if (!isArray(entry.dependencies) || !entry.dependencies.length) return;

    // Locate the built entries that are dependencies of this ignored entry
    // and are not already ignored
    const entryDependencies = this.migrate.filter(
      (e) =>
        entry.dependencies.find((d) => d.entryId === e.originalId) &&
        e.status !== 'ignore'
    );
    // For each of the built dependencies check if they are a dependency of any other entry
    for (const depEntry of entryDependencies) {
      const otherDependents = depEntry.dependencyOf.filter(
        (d) =>
          // is not from this ignored dependency
          d.entryId !== entry.originalId
      );
      // If no other dependents mark it as ignored too and recurse for its own dependencies
      if (!otherDependents.length) {
        depEntry.status = 'ignore';
        if (Logger.shouldLog('info'))
          depEntry.warnings.push(
            `Excluded ${Logger.infoText(`[dependency of entry ${this.contensis.logGuid(entry.sourceEntry)} that is not being migrated, so it has also been excluded from the migration]`)}`
          );
        this.PruneIgnoredEntryDependencies(depEntry);
      }
    }

    // HandleFailedCommitEntryDependenciesAndParents = (entry: EntryBuilder) => { }
  };
}

export default EntriesService;
